import * as XLSX from 'sheetjs-style';
import { saveAs } from 'file-saver';
import { TemplateField } from '../types/template';

// ── Download ──────────────────────────────────────────────────────────────────

export function downloadTranslationExcel(
    fields: TemplateField[],
    languages: string[],
    formName: string,
): void {
    // Sheet 1: Fields
    const fieldHeader = ['field_id', 'order', 'type',
        ...languages.flatMap(l => [`label_${l}`, `placeholder_${l}`]),
    ];
    const fieldRows = fields.map(f => [
        f.id,
        f.order,
        f.type,
        ...languages.flatMap(l => [
            f.labels?.[l] ?? (l === 'en' ? f.label : ''),
            f.placeholders?.[l] ?? (l === 'en' ? f.placeholder ?? '' : ''),
        ]),
    ]);

    // Sheet 2: Options (for choice-type fields)
    const optHeader = ['field_id', 'option_id', 'order',
        ...languages.map(l => `label_${l}`),
    ];
    const optRows: (string | number)[][] = [];
    fields.forEach(f => {
        (f.options ?? []).forEach((opt, idx) => {
            optRows.push([
                f.id,
                opt.id,
                idx + 1,
                ...languages.map(l => opt.labels?.[l] ?? (l === 'en' ? opt.label : '')),
            ]);
        });
    });

    const wb = XLSX.utils.book_new();

    const wsFields = XLSX.utils.aoa_to_sheet([fieldHeader, ...fieldRows]);
    styleHeader(wsFields, fieldHeader.length);
    XLSX.utils.book_append_sheet(wb, wsFields, 'Fields');

    const wsOptions = XLSX.utils.aoa_to_sheet([optHeader, ...optRows]);
    styleHeader(wsOptions, optHeader.length);
    XLSX.utils.book_append_sheet(wb, wsOptions, 'Options');

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const safeName = formName.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'form';
    saveAs(blob, `${safeName}_translations.xlsx`);
}

function styleHeader(ws: XLSX.WorkSheet, colCount: number): void {
    for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        if (!ws[addr]) continue;
        ws[addr].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: 'C5281F' } },
        };
    }
}

// ── Import ────────────────────────────────────────────────────────────────────

export function parseTranslationExcel(
    file: File,
    fields: TemplateField[],
): Promise<TemplateField[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => {
            try {
                const data = new Uint8Array(e.target!.result as ArrayBuffer);
                const wb = XLSX.read(data, { type: 'array' });

                // ── Fields sheet ──
                const wsFields = wb.Sheets['Fields'];
                if (wsFields) {
                    const rows = XLSX.utils.sheet_to_json<Record<string, string>>(wsFields);
                    const fieldMap = new Map(fields.map(f => [f.id, { ...f }]));

                    rows.forEach(row => {
                        const fid = row['field_id'];
                        const f = fieldMap.get(fid);
                        if (!f) return;
                        const labels: Record<string, string> = { ...(f.labels ?? {}) };
                        const placeholders: Record<string, string> = { ...(f.placeholders ?? {}) };
                        Object.entries(row).forEach(([key, val]) => {
                            const lm = key.match(/^label_(.+)$/);
                            if (lm && val) labels[lm[1]] = val;
                            const pm = key.match(/^placeholder_(.+)$/);
                            if (pm && val) placeholders[pm[1]] = val;
                        });
                        f.labels = labels;
                        f.placeholders = placeholders;
                        fieldMap.set(fid, f);
                    });

                    // ── Options sheet ──
                    const wsOptions = wb.Sheets['Options'];
                    if (wsOptions) {
                        const optRows = XLSX.utils.sheet_to_json<Record<string, string>>(wsOptions);
                        optRows.forEach(row => {
                            const fid = row['field_id'];
                            const oid = row['option_id'];
                            const f = fieldMap.get(fid);
                            if (!f?.options) return;
                            const opt = f.options.find(o => o.id === oid);
                            if (!opt) return;
                            const labels: Record<string, string> = { ...(opt.labels ?? {}) };
                            Object.entries(row).forEach(([key, val]) => {
                                const lm = key.match(/^label_(.+)$/);
                                if (lm && val) labels[lm[1]] = val;
                            });
                            opt.labels = labels;
                        });
                    }

                    resolve(Array.from(fieldMap.values()));
                } else {
                    resolve(fields);
                }
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
}
